import React, { memo, useState, useMemo, useEffect, useCallback } from "react";
import { Skeleton } from "@material-ui/lab";
import type { FC } from "react";
import { useBulkExportContext } from "../../providers";
import { useStyles } from "./DataSourceControl.styles";
import { Search } from "./Search";
import { DataSourceTree } from "./DataSourceTree";
import { IDatasourceDTO, useDataSourcesQuery, useSavedExportSettingsQuery } from "../../api";
import type { ITreeControlItem } from "./DataSourceTree";
import { Tabs, Tab, Box, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { TabPanelProps } from "../../../user/pages/UserPage";
import messages from "./DataSourceControl.messages";
import { SavedSettingsTree } from "./SavedSettingsTree";
import { ComponentPreloader } from "../../../reportng/components/ComponentPreloader";
import { BulkExportFormat, IDimension } from "../../types";
import { sortBy } from "lodash";
import { useHistory } from "react-router";
import { ISaveExportSettingsParams } from "../SaveAsBulkExportStateButton/SaveAsBulkExportStateButton.state";
import { useAddFavouriteMutation, useDeleteFavouriteMutation, useFavouriteItemsQuery } from "../../../favourites/api";
import { FavouriteItem } from "../../../favourites/types";
import { useBulkExportSettingsSave } from "../../shared/hooks/useBulkExportSettingsSave";
import { useBulkExportFavouriteQueryParams } from "../../shared/hooks/useBulkExportFavouriteQueryParams";
import produce from "immer";

function TabPanel(props: TabPanelProps) {
  const { currentTab, index, children } = props;

  return (
    <Typography component="div" role="tabpanel" hidden={currentTab !== index}>
      {currentTab === index && <Box p={3}>{children}</Box>}
    </Typography>
  );
}

export const DataSourceControl: FC = memo(() => {
  const classes = useStyles();
  const context = useBulkExportContext();
  const { favoriteBulkId, favoriteSavedId } = useBulkExportFavouriteQueryParams();
  const { data: favouriteItems = [] } = useFavouriteItemsQuery();
  const settings = useBulkExportSettingsSave();
  const { mutate: addFavourite } = useAddFavouriteMutation();
  const { mutate: removeFavourite } = useDeleteFavouriteMutation();
  const { isDataSourcesLoading, dataSources } = useDataSourcesQuery();
  const { isSavedExportSettingsLoading, savedExportSettings } = useSavedExportSettingsQuery();
  const currentTab = context?.state.tab;
  const setShowSaveNotification = context?.actions.setShowSaveNotification;
  const [searchValue, updateSearchValue] = useState("");
  const settingsWasModified = context?.state.settingsWasModified;
  const setActionAfterSaveCancel = context?.actions.setActionAfterSaveCancel;
  const { block, push } = useHistory();

  const favouriteItemId = favouriteItems.find((f) => f.id === favoriteSavedId)?.mark?.id;

  const favouriteBulkItem = favouriteItems.find((f) => f.id === favoriteBulkId);
  const favouriteBulkItemId = dataSources?.find((el) => el?.name === favouriteBulkItem?.name.split(": ")[1])?.id;

  const blockHistory = useCallback(
    (location, unblock) => {
      const mainAction = () => {
        unblock();
        push(location.pathname);
      };
      const isShowNotification = settingsWasModified && currentTab && savedExportSettings && savedExportSettings.length > 0;
      if (isShowNotification) {
        setShowSaveNotification && setShowSaveNotification(true);
        setActionAfterSaveCancel && setActionAfterSaveCancel(mainAction);
      } else {
        mainAction();
      }
    },
    [currentTab, settingsWasModified, savedExportSettings]
  );
  useEffect(() => {
    const unblock = block((location) => {
      blockHistory(location, unblock);
      return false;
    });
  }, [settingsWasModified]);

  const controldataSourcesItems = useMemo(() => {
    const filtredControlItems = dataSources?.filter((it) => !it.hidden) ?? [];
    return filtredControlItems.map<ITreeControlItem>((it) => {
      const findItem = (favItem: FavouriteItem) => favItem?.name.split(": ")[1] === it.name;
      const foundFavoriteItem = favouriteItems.find(findItem);
      return {
        id: it.id,
        name: it.name,
        parentId: it.parentId,
        groupId: it.groupId,
        disabled: !Boolean(it.identifier),
        isFavourite: Boolean(foundFavoriteItem),
        favoriteId: foundFavoriteItem?.id,
      };
    });
  }, [searchValue, dataSources, favouriteItems.length]);

  const controlSavedSettingsItems = useMemo(() => {
    const dataSourcesIdentifierDict: { [key: string]: IDatasourceDTO } = {};
    dataSources?.forEach((el) => {
      dataSourcesIdentifierDict[el.identifier] = el;
    });
    const dataSourcesIdDict: { [key: string]: IDatasourceDTO } = {};
    dataSources?.forEach((el) => {
      dataSourcesIdDict[el.id] = el;
    });
    const unicParrentsIds: string[] = Array.from(new Set(sortBy(savedExportSettings, "parentId")?.map((el) => el.cubeId)));
    const filtredControlItems: IDatasourceDTO[] = [];
    unicParrentsIds.forEach((el) => {
      filtredControlItems.push(dataSourcesIdentifierDict[el]);
      if (
        dataSourcesIdentifierDict[el].parentId &&
        !filtredControlItems.includes(dataSourcesIdDict[dataSourcesIdentifierDict[el].parentId])
      ) {
        filtredControlItems.push(dataSourcesIdDict[dataSourcesIdentifierDict[el].parentId]);
      }
    });
    const savedExportSettingsChanged =
      (savedExportSettings &&
        dataSources &&
        sortBy(savedExportSettings, "name").map((it, ind) => {
          const findItem = (favItem: FavouriteItem) => favItem?.mark?.id! === it.id;
          const foundFavoriteItemId = favouriteItems.find(findItem)?.id;
          return {
            ...it,
            id: ind + dataSources[dataSources.length - 1].id + 1,
            savingId: it.id,
            name: it.name,
            parentId: dataSources.find((el) => el.identifier === it.cubeId)!.id,
            groupId: dataSources.find((el) => el.identifier === it.cubeId)!.groupId || 0,
            disabled: false,
            favoriteId: foundFavoriteItemId,
          };
        })) ||
      [];

    return [
      ...filtredControlItems.map<ITreeControlItem>((it) => {
        const findItem = (favItem: any) => favItem?.bulkExportSettings?.id === it?.savingId;
        const foundFavoriteItemId = favouriteItems.find(findItem)?.id;
        return {
          id: it.id,
          name: it.name,
          parentId: it.parentId,
          groupId: it.groupId,
          disabled: !Boolean(it.identifier),
          favoriteId: foundFavoriteItemId,
        };
      }),
      ...savedExportSettingsChanged,
    ];
  }, [searchValue, dataSources, savedExportSettings, currentTab, favouriteItems.length]);

  const setDataSourceId = context?.actions.setDataSourceId;
  const setSavedExportSettingsId = context?.actions.setSavedExportSettingsId;
  const setCurrentTab = context?.actions.setTab;
  const selectedDataSourceId = context?.state.dataSourceId;
  const selectedSavedExportSettingsId = context?.state.savedExportSettingsId;
  const currentSavedSettings = context?.state.currentSavedSettings;
  const setDefaultDimensions = context?.actions.setDefaultDimensions;
  const setFormat = context?.actions.setFormat;

  if ((isDataSourcesLoading && isSavedExportSettingsLoading) || selectedDataSourceId === undefined) {
    return <Skeleton variant="rect" width="324px" height="100%" />;
  }

  const expandedAll = Boolean(searchValue);
  const handleChangeTab = (e: React.ChangeEvent<{}>, currentTab: number) => {
    const mainAction = () => {
      setCurrentTab!(currentTab);
      if (setDefaultDimensions && setFormat && currentTab === 0) {
        setDefaultDimensions();
        setFormat(BulkExportFormat.XLSX);
        if (dataSources && setDataSourceId) {
          const defaultDataSource = dataSources.find((it) => it.identifier === "facts_harm") ?? dataSources[0];
          setDataSourceId(defaultDataSource.id);
        }
      }
      if (currentTab === 1 && dataSources && setDataSourceId && currentSavedSettings) {
        setDataSourceId(currentSavedSettings.parentId);
      }
      updateSearchValue("");
    };
    if (!currentTab && settingsWasModified && savedExportSettings && savedExportSettings.length > 0) {
      setShowSaveNotification && setShowSaveNotification(true);
      setActionAfterSaveCancel && setActionAfterSaveCancel(mainAction);
    } else {
      mainAction();
    }
  };

  const addBulkExportToFavourite = (params?: ISaveExportSettingsParams) => {
    if (!setDataSourceId || !params?.id) {
      return;
    }
    setDataSourceId(params?.id);
    let dimensions;
    if(currentTab === 0 && !context.state.currentFavouriteSettings?.id && context.state.defaultCalendarDimension) {
      dimensions = produce(settings?.dimensions!, (draft) => {
        const updatedDimensions = draft?.map((item) => {
          if (item.id === 'calendar.default') {
            return context.state.defaultCalendarDimension as IDimension;
          }
          return item;
        });
        return updatedDimensions;
      });
    } else {
      dimensions = settings?.dimensions!
    }
    addFavourite({
      type: params?.type!,
      date: settings?.lastUpdate as string,
      name: params?.name!,
      mark: {
        parameters: {
          id: params.id!,
          parentId: params?.parentId!,
          format: settings?.format!,
          cubeId: settings?.cubeId!,
          name: params?.name!,
          dimensions,
          measures: settings?.measures!,
          settingType: 1,
        },
      },
    });
  };

  const removeBulkExportFromFavourite = (params: ISaveExportSettingsParams) => {
    removeFavourite(params as any);
  };

  return (
    <div className={classes.root}>
      <Tabs value={currentTab} onChange={handleChangeTab} indicatorColor="primary">
        <Tab key={`tab-user-info`} label={<FormattedMessage {...messages.new} />} />
        <Tab key={`tab-user-settings`} label={<FormattedMessage {...messages.saved} />} />
      </Tabs>
      <TabPanel key={`tabPanel-user-info`} index={0} currentTab={currentTab!}>
        <Search value={searchValue} onChange={updateSearchValue} />
        <DataSourceTree
          search={searchValue}
          value={selectedDataSourceId}
          onChange={setDataSourceId}
          addFavourite={addBulkExportToFavourite}
          removeFavourite={removeBulkExportFromFavourite}
          items={controldataSourcesItems}
          expandedAll={expandedAll}
          favouriteBulkItemId={favouriteBulkItemId}
        />
      </TabPanel>
      <TabPanel key={`tabPanel-user-settings`} index={1} currentTab={currentTab!}>
        {!savedExportSettings ? (
          <ComponentPreloader />
        ) : savedExportSettings.length === 0 ? (
          <Typography component="span" variant="body2">
            <FormattedMessage {...messages.noSaved} />
          </Typography>
        ) : (
          <>
            <Search value={searchValue} onChange={updateSearchValue} />
            <SavedSettingsTree
              search={searchValue}
              value={selectedSavedExportSettingsId}
              onChange={setSavedExportSettingsId}
              items={controlSavedSettingsItems as any}
              addFavourite={addFavourite}
              removeFavourite={removeBulkExportFromFavourite}
              expandedAll={expandedAll}
              favouriteItemId={favouriteItemId}
            />
          </>
        )}
      </TabPanel>
    </div>
  );
});
